import { authenticatedRestClient } from 'helpers/restclient';
import { getMasterOnBehalfOfHeader } from 'helpers/restclient/auth';

export default class VehiclesServices {
	static instance = new VehiclesServices();

	_endpoints = {
		vehicles: '/api/vehicles',
		trackVehicleView: (vehicleId) => `/api/vehicles/${vehicleId}/track_view`,
		vehiclePreview: (vehicleId) => `/api/vehicles/${vehicleId}/preview`,
		vehiclesImages: (vehicleId) => `/api/vehicles/${vehicleId}/images`,
		trackBBGAccepted: '/events/buy_back_guarantee_alert.json',
		dropOffAddresses: (dealershipId) => `/api/dealerships/${dealershipId}/dropoff_addresses`,
		eta: (vehicleId) => `/api/transportation/eta/${vehicleId}`,
		potentialBids: (vehicleId) => `/api/potential_bids/${vehicleId}`,
		vehicleServices: (vehicleId, transportation) =>
			`/api/vehicles/${vehicleId}/items?transportation=${transportation}`,
		vehicleTransportation: (vehicleId) => `/api/vehicles/${vehicleId}/transportation`,
	};

	constructor() {
		this.restClient = authenticatedRestClient;
	}

	getVehicleDetails = (vehicleId, vdpType, query, cancelToken) => {
		const endpoint = `${this._endpoints.vehicles}/${vehicleId}`;
		return this.restClient.get(
			endpoint,
			{
				...query,
				vehicle_detail: true,
				skip_tracking: true,
				vdp_type: vdpType,
			},
			{},
			cancelToken,
		);
	};

	getVehicleImages = (vehicleId, cancelToken) => {
		const endpoint = this._endpoints.vehiclesImages(vehicleId);
		return this.restClient.get(endpoint, {}, {}, cancelToken);
	};

	trackVehicleView = (vehicleId) => {
		const endpoint = this._endpoints.trackVehicleView(vehicleId);
		return this.restClient.post(endpoint, {});
	};

	getDropOffAddresses = (dealershipId) => {
		const endpoint = this._endpoints.dropOffAddresses(dealershipId);
		return this.restClient.get(endpoint, {});
	};

	getEta = (vehicleId, loggedUser, distance = null) => {
		const queryParams = new URLSearchParams({ ...(distance && { distance }) });
		const endpoint = `${this._endpoints.eta(vehicleId)}?${queryParams}`;
		return this.restClient.get(endpoint, {});
	};

	getPotentialBids = (
		{ vehicleId, isAuction, dropoffAddressId, paymentMethodType, floorPlanCompanyId },
		masterOnBehalfOf,
	) => {
		const endpoint = this._endpoints.potentialBids(vehicleId);
		const params = {};
		if (isAuction) params.context = 'timed_bid';
		if (dropoffAddressId) params.dropoff_address_id = dropoffAddressId;
		if (paymentMethodType) params.payment_method_type = paymentMethodType;
		if (floorPlanCompanyId) params.floor_plan_company_id = floorPlanCompanyId;
		return this.restClient.get(endpoint, params, getMasterOnBehalfOfHeader(masterOnBehalfOf));
	};

	getVehicleServices = ({ vehicleId, isAuction, dropoffAddressId, amount }, masterOnBehalfOf) => {
		const endpoint = this._endpoints.vehicleServices(vehicleId, true);
		const params = {};
		if (isAuction) params.context = 'timed_bid';
		if (dropoffAddressId) params.dropoff_address_id = dropoffAddressId;
		if (amount) params.amount = amount;
		return this.restClient.get(endpoint, params, getMasterOnBehalfOfHeader(masterOnBehalfOf));
	};

	trackHighOfferWarningAccepted = (vehicleId) => {
		const endpoint = this._endpoints.trackBBGAccepted;
		return this.restClient.post(endpoint, { vehicle_id: vehicleId });
	};

	getVehiclePreview = (vehiclePreviewPayload, masterOnBehalfOf, dropoffAddressId) => {
		const { vehicleId, ...payload } = vehiclePreviewPayload;
		const endpoint = this._endpoints.vehiclePreview(vehicleId);
		return this.restClient.get(
			endpoint,
			{ ...payload, dropoff_address_id: dropoffAddressId },
			getMasterOnBehalfOfHeader(masterOnBehalfOf),
		);
	};

	getVehicleTransportation = (vehicleId, vehicle_detail, dropoff_address_id, masterOnBehalfOf) => {
		const endpoint = this._endpoints.vehicleTransportation(vehicleId);
		return this.restClient.get(
			endpoint,
			{ vehicle_detail, dropoff_address_id },
			getMasterOnBehalfOfHeader(masterOnBehalfOf),
		);
	};
}
